import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';

type QcaDashboardStat = {
  label: string;
  value: string;
  suffix?: string;
  icon: string;
  tone: 'blue' | 'green' | 'red' | 'amber' | 'violet';
};

type QcaDashboardRow = {
  code: string;
  name: string;
  registeredAddress: string;
  contactPerson: string;
  mobile: string;
  email: string;
  state: string;
  totalCapacityMw: number;
  pssCount: number;
  generatorCount: number;
  unitCount: number;
  registrationDate: string;
  approvalStatus: 'Approved' | 'Pending' | 'Rejected';
  status: 'Active' | 'Inactive';
};

type PssCatalogItem = {
  code: string;
  name: string;
  state: string;
  installedCapacityMw: number;
  status: 'Active' | 'Inactive';
};

type QcaPssMapping = {
  pssCode: string;
  mappedOn: string;
};

type MappedPssRow = PssCatalogItem & {
  mappedOn: string;
};

type QcaDashboardMock = {
  header: {
    eyebrow: string;
    title: string;
    description: string;
  };
  stats: QcaDashboardStat[];
  filters: {
    status: string[];
    approvalStatus: string[];
    state: string[];
  };
  rows: QcaDashboardRow[];
  pssCatalog: PssCatalogItem[];
  qcaPssMappings: Record<string, QcaPssMapping[]>;
};

const DEFAULT_DATA: QcaDashboardMock = {
  header: {
    eyebrow: 'Registration Administration',
    title: 'Manage QCA',
    description: 'Review QCA registrations and manage linked PSS records.'
  },
  stats: [],
  filters: {
    status: ['All'],
    approvalStatus: ['All'],
    state: ['All']
  },
  rows: [],
  pssCatalog: [],
  qcaPssMappings: {}
};

@Component({
  selector: 'app-qca-registration',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './qca-registration.component.html',
  styleUrls: ['./qca-registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QcaRegistrationComponent {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  readonly pageData = signal<QcaDashboardMock>(DEFAULT_DATA);
  readonly searchTerm = signal('');
  readonly selectedStatus = signal('All');
  readonly selectedApprovalStatus = signal('All');
  readonly selectedState = signal('All');
  readonly minCapacity = signal('');
  readonly maxCapacity = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(8);
  readonly isManagePssModalOpen = signal(false);
  readonly selectedQcaCode = signal<string | null>(null);
  readonly activePssTab = signal<'available' | 'mapped'>('available');
  readonly availablePssSearch = signal('');
  readonly mappedPssSearch = signal('');
  readonly selectedPssState = signal('All');
  readonly selectedAvailablePssCodes = signal<string[]>([]);
  readonly selectedMappedPssCodes = signal<string[]>([]);

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pageData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pageData().header.description);
  readonly eyebrow = computed(() => this.pageData().header.eyebrow);
  readonly stats = computed(() => this.pageData().stats);
  readonly filters = computed(() => this.pageData().filters);
  readonly rows = computed(() => this.pageData().rows);
  readonly pssCatalog = computed(() => this.pageData().pssCatalog);
  readonly qcaPssMappings = computed(() => this.pageData().qcaPssMappings);
  readonly selectedQca = computed(() => this.rows().find((row) => row.code === this.selectedQcaCode()) || null);
  readonly mappedPssRows = computed<MappedPssRow[]>(() => {
    const qcaCode = this.selectedQcaCode();
    if (!qcaCode) {
      return [];
    }

    const mappingByCode = new Map(
      (this.qcaPssMappings()[qcaCode] || []).map((mapping) => [mapping.pssCode, mapping.mappedOn])
    );

    return this.pssCatalog()
      .filter((item) => mappingByCode.has(item.code))
      .map((item) => ({
        ...item,
        mappedOn: mappingByCode.get(item.code) || '-'
      }));
  });
  readonly availablePssRows = computed(() => {
    const search = this.availablePssSearch().trim().toLowerCase();
    const state = this.selectedPssState();
    const mappedCodes = new Set(this.mappedPssRows().map((item) => item.code));

    return this.pssCatalog().filter((item) => {
      const matchesSearch = !search || [item.code, item.name].some((value) => value.toLowerCase().includes(search));
      const matchesState = state === 'All' || item.state === state;
      const isNotMapped = !mappedCodes.has(item.code);
      return matchesSearch && matchesState && isNotMapped;
    });
  });
  readonly filteredMappedPssRows = computed(() => {
    const search = this.mappedPssSearch().trim().toLowerCase();

    return this.mappedPssRows().filter((item) =>
      !search || [item.code, item.name].some((value) => value.toLowerCase().includes(search))
    );
  });
  readonly pssStateOptions = computed(() => ['All', ...new Set(this.pssCatalog().map((item) => item.state))]);
  readonly mappedCount = computed(() => this.mappedPssRows().length);

  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    const status = this.selectedStatus();
    const approvalStatus = this.selectedApprovalStatus();
    const selectedState = this.selectedState();
    const minCapacity = Number(this.minCapacity());
    const maxCapacity = Number(this.maxCapacity());
    const hasMin = this.minCapacity().trim() !== '' && !Number.isNaN(minCapacity);
    const hasMax = this.maxCapacity().trim() !== '' && !Number.isNaN(maxCapacity);

    return this.rows().filter((row) => {
      const matchesSearch = !search || [
        row.code,
        row.name,
        row.registeredAddress,
        row.contactPerson,
        row.mobile,
        row.email
      ].some((value) => value.toLowerCase().includes(search));
      const matchesStatus = status === 'All' || row.status === status;
      const matchesApproval = approvalStatus === 'All' || row.approvalStatus === approvalStatus;
      const matchesState = selectedState === 'All' || row.state === selectedState;
      const matchesMin = !hasMin || row.totalCapacityMw >= minCapacity;
      const matchesMax = !hasMax || row.totalCapacityMw <= maxCapacity;

      return matchesSearch && matchesStatus && matchesApproval && matchesState && matchesMin && matchesMax;
    });
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredRows().length / this.pageSize())));
  readonly paginatedRows = computed(() => {
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });
  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingText = computed(() => {
    const total = this.filteredRows().length;
    if (total === 0) {
      return 'Showing 0 entries';
    }
    const start = ((this.currentPage() - 1) * this.pageSize()) + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `Showing ${start} to ${end} of ${total} entries`;
  });

  constructor() {
    this.http.get<QcaDashboardMock>('json/qca-management.mock.json').subscribe({
      next: (data) => this.pageData.set(data),
      error: () => this.pageData.set(DEFAULT_DATA)
    });
  }

  updateSearch(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  updateStatus(value: string): void {
    this.selectedStatus.set(value);
    this.currentPage.set(1);
  }

  updateApprovalStatus(value: string): void {
    this.selectedApprovalStatus.set(value);
    this.currentPage.set(1);
  }

  updateState(value: string): void {
    this.selectedState.set(value);
    this.currentPage.set(1);
  }

  updateMinCapacity(value: string): void {
    this.minCapacity.set(value);
    this.currentPage.set(1);
  }

  updateMaxCapacity(value: string): void {
    this.maxCapacity.set(value);
    this.currentPage.set(1);
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.selectedStatus.set('All');
    this.selectedApprovalStatus.set('All');
    this.selectedState.set('All');
    this.minCapacity.set('');
    this.maxCapacity.set('');
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    this.currentPage.set(Math.min(Math.max(page, 1), this.totalPages()));
  }

  openManagePssModal(qcaCode: string): void {
    this.selectedQcaCode.set(qcaCode);
    this.activePssTab.set('available');
    this.availablePssSearch.set('');
    this.mappedPssSearch.set('');
    this.selectedPssState.set('All');
    this.selectedAvailablePssCodes.set([]);
    this.selectedMappedPssCodes.set([]);
    this.isManagePssModalOpen.set(true);
  }

  closeManagePssModal(): void {
    this.isManagePssModalOpen.set(false);
    this.selectedMappedPssCodes.set([]);
  }

  switchPssTab(tab: 'available' | 'mapped'): void {
    this.activePssTab.set(tab);
    this.selectedAvailablePssCodes.set([]);
    this.selectedMappedPssCodes.set([]);
  }

  updateAvailablePssSearch(value: string): void {
    this.availablePssSearch.set(value);
  }

  updateMappedPssSearch(value: string): void {
    this.mappedPssSearch.set(value);
  }

  updatePssStateFilter(value: string): void {
    this.selectedPssState.set(value);
  }

  toggleAvailableSelection(pssCode: string, checked: boolean): void {
    const current = this.selectedAvailablePssCodes();
    this.selectedAvailablePssCodes.set(
      checked ? [...current, pssCode] : current.filter((code) => code !== pssCode)
    );
  }

  addSelectedPss(): void {
    const selectedCodes = this.selectedAvailablePssCodes();
    if (selectedCodes.length === 0) {
      return;
    }

    selectedCodes.forEach((pssCode) => this.mapPss(pssCode, false));
    this.selectedAvailablePssCodes.set([]);
    this.activePssTab.set('mapped');
  }

  mapPss(pssCode: string, switchToMappedTab = true): void {
    const qcaCode = this.selectedQcaCode();
    if (!qcaCode) {
      return;
    }

    const nextMappings = { ...this.qcaPssMappings() };
    const currentMappings = [...(nextMappings[qcaCode] || [])];
    if (currentMappings.some((item) => item.pssCode === pssCode)) {
      return;
    }

    currentMappings.push({
      pssCode,
      mappedOn: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
    });
    nextMappings[qcaCode] = currentMappings;
    this.pageData.update((data) => ({ ...data, qcaPssMappings: nextMappings }));
    if (switchToMappedTab) {
      this.activePssTab.set('mapped');
    }
  }

  toggleMappedSelection(pssCode: string, checked: boolean): void {
    const current = this.selectedMappedPssCodes();
    this.selectedMappedPssCodes.set(
      checked ? [...current, pssCode] : current.filter((code) => code !== pssCode)
    );
  }

  removeMappedPss(pssCode: string): void {
    const qcaCode = this.selectedQcaCode();
    if (!qcaCode) {
      return;
    }

    const nextMappings = { ...this.qcaPssMappings() };
    nextMappings[qcaCode] = (nextMappings[qcaCode] || []).filter((item) => item.pssCode !== pssCode);
    this.pageData.update((data) => ({ ...data, qcaPssMappings: nextMappings }));
    this.selectedMappedPssCodes.set(this.selectedMappedPssCodes().filter((code) => code !== pssCode));
  }

  removeSelectedMappedPss(): void {
    const qcaCode = this.selectedQcaCode();
    if (!qcaCode || this.selectedMappedPssCodes().length === 0) {
      return;
    }

    const selectedCodes = new Set(this.selectedMappedPssCodes());
    const nextMappings = { ...this.qcaPssMappings() };
    nextMappings[qcaCode] = (nextMappings[qcaCode] || []).filter((item) => !selectedCodes.has(item.pssCode));
    this.pageData.update((data) => ({ ...data, qcaPssMappings: nextMappings }));
    this.selectedMappedPssCodes.set([]);
  }

  formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }
}
